import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type DateRange, type Locale, normalizeDateToStartOfDay, normalizeDateToEndOfDay } from "@/shared/utils";
import { MONTHS_RU, MONTHS_KK, MONTHS_EN } from "@/shared/consts";
import { usePagination } from "@/shared/hooks";
import { useGetContractorPayrollListQuery } from "../api";
import type { ContractorPayrollListItem, ContractorPayrollAggregatedStatus } from "../types";
import { useContractorPayrollSort, toggleSort } from "./useContractorPayrollSort";
import type { SortKey, SortConfig } from "./useContractorPayrollSort";

export interface StatusOption {
  label: string;
  value: ContractorPayrollAggregatedStatus | null;
}

export function useContractorPayrollListPage() {
  const { i18n, t } = useTranslation("ContractorPayrollPage");
  const locale = i18n.language as Locale;

  const { data: response, isLoading, isError } = useGetContractorPayrollListQuery();

  const data = response?.results;

  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });
  const [statusFilter, setStatusFilter] = useState<ContractorPayrollAggregatedStatus | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig<SortKey> | null>(null);

  const statusOptions = useMemo<StatusOption[]>(
    () => [
      { label: t("filters.allStatuses"), value: null },
      { label: t("status.all_pending"), value: "all_pending" },
      { label: t("status.partially_approved"), value: "partially_approved" },
      { label: t("status.all_approved"), value: "all_approved" },
      { label: t("status.partially_paid"), value: "partially_paid" },
      { label: t("status.all_paid"), value: "all_paid" },
    ],
    [t],
  );

  const activeFilters = useMemo(
    () => !!search.trim() || !!dateRange.start || !!dateRange.end || statusFilter !== null,
    [search, dateRange, statusFilter],
  );

  function handleSort(key: SortKey) {
    setSortConfig((prev) => toggleSort(prev, key));
  }

  const filteredItems = useMemo(() => {
    if (!data) return [];

    return data.filter((item) => {
      const trimmedSearch = search.trim();
      if (trimmedSearch) {
        const searchLower = trimmedSearch.toLowerCase();
        const yearStr = String(item.year);
        const monthStr = String(item.month);
        const monthRu = MONTHS_RU[item.month - 1]?.toLowerCase() || "";
        const monthKk = MONTHS_KK[item.month - 1]?.toLowerCase() || "";
        const monthEn = MONTHS_EN[item.month - 1]?.toLowerCase() || "";

        const matchesSearch =
          yearStr.includes(trimmedSearch) ||
          monthRu.includes(searchLower) ||
          monthKk.includes(searchLower) ||
          monthEn.includes(searchLower) ||
          monthStr.includes(trimmedSearch);

        if (!matchesSearch) return false;
      }

      if (statusFilter && item.aggregated_status !== statusFilter) {
        return false;
      }

      if (dateRange.start || dateRange.end) {
        const monthStart = normalizeDateToStartOfDay(new Date(item.year, item.month - 1, 1));
        const monthEnd = normalizeDateToEndOfDay(new Date(item.year, item.month, 0));
        const rangeStart = dateRange.start ? normalizeDateToStartOfDay(dateRange.start) : null;
        const rangeEnd = dateRange.end ? normalizeDateToEndOfDay(dateRange.end) : null;
        const hasIntersection = (!rangeStart || rangeStart <= monthEnd) && (!rangeEnd || rangeEnd >= monthStart);
        if (!hasIntersection) return false;
      }

      return true;
    });
  }, [data, search, statusFilter, dateRange]);

  const sortedItems = useContractorPayrollSort(filteredItems, sortConfig);

  const pagination = usePagination(sortedItems.length, 10, [search, statusFilter, dateRange, sortConfig]);
  const pageItems = sortedItems.slice(pagination.startIndex, pagination.endIndex);

  function handleResetFilters() {
    setSearch("");
    setDateRange({ start: null, end: null });
    setStatusFilter(null);
    pagination.setPage(1);
  }

  return {
    data,
    isLoading,
    isError,
    filteredItems: sortedItems,
    pagination,
    pageItems,
    sortConfig,
    handleSort,
    search,
    setSearch,
    dateRange,
    setDateRange,
    statusFilter,
    setStatusFilter,
    statusOptions,
    activeFilters,
    handleResetFilters,
    locale,
    t,
  };
}
